import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import DocLayout from '../components/docs/DocLayout'
import Specimen from '../components/library/Specimens'
import { componentCategories, components, type ComponentDoc } from '@/content/components'

type Filter = ComponentDoc['category'] | 'All'

export default function ComponentsPage() {
  const [filter, setFilter] = useState<Filter>('All')
  const [query, setQuery] = useState('')

  const results = useMemo(() => {
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
    return components.filter(
      (component) =>
        (filter === 'All' || component.category === filter) &&
        words.every((word) =>
          `${component.name} ${component.summary} ${component.category} ${component.uswds.join(' ')}`.toLowerCase().includes(word),
        ),
    )
  }, [filter, query])

  const groups = componentCategories
    .map((category) => ({ category, items: results.filter((item) => item.category === category) }))
    .filter((group) => group.items.length)

  return (
    <DocLayout
      eyebrow="Components"
      title="Components"
      wide
      intro={
        <p>
          Accessible, themeable building blocks for government websites. Every component reads the semantic color
          roles, so it follows light and dark mode and every mission theme. Each page maps it to the USWDS components it
          replaces.
        </p>
      }
    >
      <div className="lib-filters">
        <div className="mds-search lib-filters__search" role="search">
          <label className="mds-visually-hidden" htmlFor="component-search">
            Search components
          </label>
          <input
            id="component-search"
            className="mds-search__input"
            type="search"
            placeholder="Search components or USWDS names"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        <div className="mds-segmented" role="group" aria-label="Filter by category">
          {(['All', ...componentCategories] as Filter[]).map((category) => (
            <button
              key={category}
              type="button"
              className="mds-segmented__option"
              aria-pressed={filter === category}
              onClick={() => setFilter(category)}
            >
              {category}
            </button>
          ))}
        </div>
        <p className="mds-muted lib-filters__status" role="status" aria-live="polite">
          {results.length === components.length
            ? `Showing all ${components.length} components`
            : `Showing ${results.length} of ${components.length} components`}
        </p>
      </div>

      {groups.length === 0 && (
        <div className="lib-empty">
          <p className="mds-h3">No components match “{query}”.</p>
          <button
            type="button"
            className="mds-button mds-button--secondary"
            onClick={() => {
              setQuery('')
              setFilter('All')
            }}
          >
            Clear filters
          </button>
        </div>
      )}

      {groups.map((group) => (
        <section key={group.category} className="lib-group" aria-labelledby={`group-${group.category}`}>
          <h2 id={`group-${group.category}`} className="mds-h2">
            {group.category}
          </h2>
          <ul className="lib-grid">
            {group.items.map((component) => (
              <li key={component.id} className="lib-card">
                <div className="lib-card__specimen" inert aria-hidden="true">
                  <Specimen id={component.id} mini />
                </div>
                <div className="lib-card__text">
                  <h3 className="mds-card__title">
                    <Link className="mds-card__link" to={`/components/${component.id}`}>
                      {component.name}
                    </Link>
                  </h3>
                  <p className="mds-muted">{component.summary}</p>
                  {component.uswds.length > 0 && <p className="lib-card__uswds">Replaces USWDS {component.uswds.join(', ')}</p>}
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </DocLayout>
  )
}
